// Aggregators: how a card turns the period's games into its numbers. A card
// plugin implements `Aggregator` and registers it with the store
// (`GameStore.registerAggregator`); the store hands every game of the last 30
// days to every registered aggregator, once, and keeps each one's result.
// Adding a card adds an aggregator in the card's own plugin: no shared
// function or shared result type changes.
import { inPeriod, periodStart } from './classification.ts';
import type { GameFacts } from './facts.ts';

export type AggregatorId = string & { readonly __brand: 'AggregatorId' };

/** An aggregator's id; refuses an empty string. */
export function aggregatorId(id: string): AggregatorId {
  if (!id) throw new Error('an aggregator id must not be empty');
  return id as AggregatorId;
}

/** The period a run covers: the last 30 days, the library filter's window. */
export interface PeriodContext {
  /** Epoch milliseconds, exclusive. */
  from: number;
  /** Epoch milliseconds, inclusive. */
  to: number;
}

/** One run: told each game of the period once, then asked for the result. */
export interface Accumulator<R> {
  add(game: GameFacts): void;
  result(): R;
}

/** A card's computation: a fresh accumulator per run. */
export interface Aggregator<R> {
  readonly id: AggregatorId;
  start(context: PeriodContext): Accumulator<R>;
}

/** The period ending `now`: the last 30 days (`inPeriod`). */
export function periodContext(now: number): PeriodContext {
  return { from: periodStart('30d', now), to: now };
}

export interface AggregatorFailure {
  id: AggregatorId;
  error: unknown;
}

/**
 * Runs aggregators over the games of the period ending `now`, one pass for
 * all of them; older and later games are skipped. An aggregator that throws
 * is left out of the results and reported, so one card's bug blanks only
 * that card.
 */
export function runAggregators(
  aggregators: Iterable<Aggregator<unknown>>,
  games: Iterable<GameFacts>,
  now: number,
  onFailure?: (failure: AggregatorFailure) => void,
): Map<AggregatorId, unknown> {
  const context = periodContext(now);
  const runs = new Map<AggregatorId, Accumulator<unknown>>();
  const fail = (id: AggregatorId, error: unknown) => {
    runs.delete(id);
    onFailure?.({ id, error });
  };
  for (const aggregator of aggregators) {
    try {
      runs.set(aggregator.id, aggregator.start(context));
    } catch (error) {
      fail(aggregator.id, error);
    }
  }
  for (const game of games) {
    if (!inPeriod(game.playedAt, '30d', now)) continue;
    for (const [id, run] of runs) {
      try {
        run.add(game);
      } catch (error) {
        fail(id, error);
      }
    }
  }
  const results = new Map<AggregatorId, unknown>();
  for (const [id, run] of runs) {
    try {
      results.set(id, run.result());
    } catch (error) {
      fail(id, error);
    }
  }
  return results;
}

/** One aggregator over the games: for tests, and for a caller that needs one result without the store. */
export function aggregateOnce<R>(aggregator: Aggregator<R>, games: Iterable<GameFacts>, now: number): R {
  let failure: unknown;
  const results = runAggregators([aggregator as Aggregator<unknown>], games, now, (f) => (failure = f.error));
  if (!results.has(aggregator.id)) throw failure ?? new Error(`aggregator ${aggregator.id} produced no result`);
  return results.get(aggregator.id) as R;
}

/**
 * An accumulator that keeps the period's games and computes once at the end:
 * for a card whose numbers need the whole list (a sort, a series in time
 * order) rather than a running count.
 */
export function collect<R>(compute: (games: GameFacts[]) => R): Accumulator<R> {
  const games: GameFacts[] = [];
  return { add: (game) => void games.push(game), result: () => compute(games) };
}

/** The mean of some numbers; undefined for none. */
export function average(values: readonly number[]): number | undefined {
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : undefined;
}
